/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: caoran
 * @LastEditTime: 2024-11-25 17:44:19
 */

import './index.scss'
import React, { useEffect, useRef, useState } from 'react'
import { Button, Dropdown, Modal } from 'antd'
import type { MenuProps } from 'antd'
import { v4 as uuid } from 'uuid'
import { useAppSelector, useSensorsRole, useVip } from '../../../app/hooks'
import sensors from '../../../utils/sensors'
import RequireVipDialog from './components/RequireVipDialog'
import UploadDialog from './components/UploadDialog'
import vip_download_excel_icon from '../../../images/vip_download_excel.png'
import { CloudUploadOutlined } from '@ant-design/icons'

// 扩展 input 元素属性以支持 webkitdirectory
declare module 'react' {
  interface InputHTMLAttributes<T> extends HTMLAttributes<T> {
    webkitdirectory?: string
  }
}

interface UploadDialogRef {
  startUpload: (files: File[]) => void
}

const maxFileSize = 1024 * 1024 * 100 // 100MB
const maxFileLength = 1000 // 单次上传最大文件数
const mimes = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'video/mp4',
  'video/mov',
  'video/avi'
]

const getMenuItems = (
  fileUploadRef: React.RefObject<HTMLInputElement | null>,
  folderUploadRef: React.RefObject<HTMLInputElement | null>,
  clickEvent: (path: string) => void
): MenuProps['items'] => {
  return [
    {
      key: 'file',
      label: '上传照片/视频',
      onClick: () => {
        clickEvent('uploadLocalPhotos/photovideo')
        fileUploadRef.current?.click()
      }
    },
    {
      key: 'folder',
      label: '上传文件夹',
      onClick: () => {
        clickEvent('uploadLocalPhotos/folder')
        folderUploadRef.current?.click()
      }
    },
    { type: 'divider' as const, key: 'divider' },
    {
      key: 'tip',
      label: (
        <span style={{ fontSize: '12px', color: '#848484' }}>
          最多只能上传1000张照片/视频
        </span>
      ),
      disabled: true
    }
  ]
}

const UploadPhotosButton: React.FC = () => {
  const groupID = useAppSelector((state) => state.group.groupId)
  const user = useAppSelector((state) => state.main.user)
  const role = useSensorsRole()
  const [fileList, setFileList] = useState<File[]>([])
  const [vipRequiredVisible, setVipRequiredVisible] = useState(false)
  const [uploadDialogVisible, setUploadDialogVisible] = useState(false)
  const fileUploadRef = React.useRef<HTMLInputElement>(null)
  const folderUploadRef = React.useRef<HTMLInputElement>(null)
  const uploadDialogRef = React.useRef<UploadDialogRef>(null)
  const vipInfo = useVip()
  const isSpaceOpen = useAppSelector(
    (state) => (state as any).mySpace?.personalSpace?.isSpaceOpen ?? false
  )
  const taskRef = useRef<string | null>(null)

  const sensorNavMenuClickEvent = (path: string) => {
    sensors.track('web_workgroup_nav_menu_cli', {
      path,
      groupID,
      isGroupVIP: vipInfo.isVip,
      bool1: isSpaceOpen
    })
  }

  // debug埋点
  const sensorTrackUploadProgress = (properties: Record<string, any>) => {
    sensors.track('web_upload_photos_debug', {
      ...properties,
      string1: user?.mobile,
      taskID: taskRef.current
    })
  }

  const filterTypeAndSize = (inputFiles: File[]) => {
    // 过滤掉不支持的文件类型，以及超过100M的文件
    const filteredFiles = inputFiles.filter((file) => mimes.includes(file.type))
    const lteSizeFiles = filteredFiles.filter(
      (file) => file.size <= maxFileSize
    )
    if (filteredFiles.length !== inputFiles.length) {
      let title = `共识别到${filteredFiles.length}个照片/视频。`
      if (lteSizeFiles.length !== filteredFiles.length) {
        title += `${filteredFiles.length - lteSizeFiles.length}个文件超出100M将被忽略。`
      }
      Modal.info({
        title,
        okText: '继续上传',
        onOk: () => {
          checkFilesLength(lteSizeFiles)
        }
      })
    } else {
      checkFilesLength(lteSizeFiles)
    }
  }

  const handleCheckFileLength = (inputFileList: File[]) => {
    if (inputFileList.length > maxFileLength) {
      Modal.warning({
        title: `选择内容超过${maxFileLength}张，无法上传`,
        okText: '我知道了',
        onOk: () => {
          sensors.track('web_get_action_bigbatch_upload_tips', {
            actionType: 'overLimit',
            content: `选择内容超过${maxFileLength}张，无法上传`,
            groupID,
            role,
            clickItem: 'know'
          })
        }
      })
      return false
    }
    return true
  }

  // 检查剩余照片数
  const checkFilesLength = (inputFiles: File[]) => {
    if (inputFiles.length === 0) return
    if (handleCheckFileLength(inputFiles) === false) return
    setFileList(inputFiles)
  }

  useEffect(() => {
    if (fileList.length > 0) {
      if (vipInfo.benefits.batchUpload) {
        setVipRequiredVisible(false)
        setUploadDialogVisible(true)
        taskRef.current = uuid()
        uploadDialogRef.current?.startUpload(fileList)
      } else {
        setVipRequiredVisible(true)
      }
    }
  }, [vipInfo.benefits.batchUpload, fileList])

  return (
    <>
      <Dropdown
        menu={{
          items: getMenuItems(
            fileUploadRef,
            folderUploadRef,
            sensorNavMenuClickEvent
          )
        }}
      >
        <Button className="upload-photos-btn" type="primary">
          <CloudUploadOutlined />
          上传照片/视频
          <img
            alt=""
            src={vip_download_excel_icon}
            style={{
              marginLeft: 4,
              width: 20,
              height: 20
            }}
          />
        </Button>
      </Dropdown>
      <input
        hidden
        ref={fileUploadRef}
        name="file"
        type="file"
        accept={mimes.join(', ')}
        multiple
        value={[]}
        onChange={(e) => filterTypeAndSize(Array.from(e.target.files || []))}
      />
      <input
        hidden
        ref={folderUploadRef}
        name="folder"
        type="file"
        accept={mimes.join(', ')}
        webkitdirectory="true"
        multiple
        value={[]}
        onChange={(e) => filterTypeAndSize(Array.from(e.target.files || []))}
      />
      <RequireVipDialog
        fileList={fileList}
        visible={vipRequiredVisible}
        onClose={() => {
          setVipRequiredVisible(false)
          setFileList([])
        }}
      />
      <UploadDialog
        ref={uploadDialogRef}
        visible={uploadDialogVisible}
        fileList={fileList}
        debugEvent={sensorTrackUploadProgress}
        onClose={() => {
          setFileList([])
          setUploadDialogVisible(false)
        }}
      />
    </>
  )
}

export default UploadPhotosButton
